import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import performRequest from "../api/request";
import { FcRatings } from "react-icons/fc";

const EmployeeTestOverview = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  /* ------------------ STATE ------------------ */

  const [summary, setSummary] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [traineeSummary, setTraineeSummary] = useState(null);

  /* ------------------ FETCH DATA ------------------ */

  useEffect(() => {
    performRequest("GET", `/test/employee-evaluation-overview/${id}`)
      .then((res) => {
        const data = res?.data || res;

        setSummary(data.summary);
        setAttempts(data.attempts || []);
      })
      .catch((err) => {
        console.error("Failed to load overview", err);
      });
  }, []);

  useEffect(() => {
    performRequest("GET", `/test/trainee-evaluation-overview/${id}`)
      .then((res) => {
        const data = res?.data || res;

        setTraineeSummary(data);
        // setTraineeAttempts(data.attempts || []);
      })
      .catch((err) => {
        console.error("Failed to load overview", err);
      });
  }, []);

  /* ------------------ DATE HELPERS ------------------ */

  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();

  const parseDate = (dateStr) => {
    return new Date(dateStr.replace(" ", "T"));
  };

  /* ------------------ FILTER LOGIC ------------------ */

  const monthlyAttempts = useMemo(() => {
    return attempts.filter((a) => {
      if (!a.testDate) return false;
      const d = parseDate(a.testDate);
      return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
    });
  }, [attempts]);

  const yearlyAttempts = useMemo(() => {
    return attempts.filter((a) => {
      if (!a.testDate) return false;
      const d = parseDate(a.testDate);
      return d.getFullYear() === currentYear;
    });
  }, [attempts]);

  /* ------------------ LOADING ------------------ */

  if (!summary) {
    return <div className="p-4 text-muted">Loading overview...</div>;
  }

  /* ------------------ UI ------------------ */

  return (
    <div className="page">
      <div className="container-xl">
        {/* HEADER */}
        <div className="page-header mb-4 mt-2">
          <h2 className="page-title"> <FcRatings className="fs-2  me-1" />  Evaluation Overview</h2>
          <div className="text-muted">Monthly & Yearly Performance</div>
        </div>

        {/* SUMMARY */}
        <div className="row row-cards mb-4">
          <StatCard label="Attempts" value={summary.totalAttempts} />
          <StatCard
            label="Correct"
            value={summary.totalCorrect}
            color="text-success"
          />
          <StatCard
            label="Wrong"
            value={summary.totalWrong}
            color="text-danger"
          />
          <StatCard
            label="Skipped"
            value={summary.totalSkipped}
            color="text-warning"
          />
          {traineeSummary && (
            <div className="card card-md">
              <div className="card-body">
                {/* Header */}
                <div className="row mb-4">
                  <div className="col-12">
                    <h3 className="card-title">Training Test Summary</h3>
                  </div>
                </div>

                {/* Top Stats Grid */}
                <div className="row mb-4">
                  <div className="col-sm-6 col-lg-4">
                    <div className="text-muted">Total Attempts</div>
                    <div className="fw-bold fs-2">
                      {traineeSummary?.totalAttempts} /{" "}
                      {traineeSummary?.maxAttempts}
                    </div>
                  </div>

                  <div className="col-sm-6 col-lg-4 mt-3 mt-sm-0">
                    <div className="text-muted">Attempts Remaining</div>
                    <div className="fw-bold fs-2">
                      {traineeSummary?.attemptsRemaining}
                    </div>
                  </div>
                </div>

                <hr />

                {/* Attempt Details Grid */}
                <div className="row mb-2">
                  <div className="col-12">
                    <div className="text-muted mb-2">Attempt Details</div>
                  </div>
                </div>

                {traineeSummary?.attempts?.map((attempt) => (
                  <div
                    key={attempt.attemptId}
                    className="row mb-2 p-3 rounded"
                    style={{ backgroundColor: "#f8fafc" }}
                  >
                    <div className="col-sm-6 col-lg-4">
                      <div className="fw-bold">
                        Attempt {attempt?.attemptNumber}
                      </div>
                      <div className="text-muted fs-5">
                        Score: {attempt?.correctAnswers} /{" "}
                        {attempt?.totalQuestions || 0}
                      </div>
                    </div>

                    <div className="col-sm-3 col-lg-4 mt-2 mt-sm-0">
                      <div className="text-muted">Percentage</div>
                      <div className="fw-bold fs-3">{attempt?.percentage}%</div>
                    </div>

                    <div className="col-sm-3 col-lg-4 mt-2 mt-sm-0 text-sm-end">
                      <div className="text-muted">Status</div>
                      <span
                        className={`badge fs-5 ${
                          attempt.percentage >= 60
                            ? "bg-green text-white"
                            : "bg-red text-white"
                        }`}
                      >
                        {attempt.percentage >= 60 ? "PASS" : "FAIL"}
                      </span>
                    </div>
                  </div>
                ))}

                <hr />

                {/* Overall Result Grid */}
                <div className="row align-items-center">
                  <div className="col-sm-8">
                    <div className="text-muted">Overall Result</div>
                    <div className="fw-bold fs-2">
                      Average:{" "}
                      {traineeSummary?.overallResult?.averagePercentage}%
                    </div>
                  </div>

                  <div className="col-sm-4 mt-2 mt-sm-0 text-sm-end">
                    <span
                      className={`badge fs-2 text-white ${
                        traineeSummary?.overallResult?.status === "PASS"
                          ? "bg-green"
                          : "bg-red"
                      }`}
                    >
                      {traineeSummary?.overallResult?.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* MONTHLY */}
        <Section title="📅 Monthly Performance">
          {monthlyAttempts.length ? (
            monthlyAttempts.map((a) => (
              <AttemptCard key={a.attemptId} attempt={a} />
            ))
          ) : (
            <Empty />
          )}
        </Section>

        {/* YEARLY */}
        <Section title="📆 Yearly Performance">
          {yearlyAttempts.length ? (
            yearlyAttempts.map((a) => (
              <AttemptCard key={a.attemptId} attempt={a} />
            ))
          ) : (
            <Empty />
          )}
        </Section>

        <div className="text-end">
          <button
            onClick={() => navigate("/my-scoreboard")}
            className="btn btn-primary mb-1"
          >
            Back to Rankings
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------ COMPONENTS ------------------ */

const Section = ({ title, children }) => (
  <div className="mb-4">
    <h3 className="mb-3">{title}</h3>
    <div className="row">{children}</div>
  </div>
);

const AttemptCard = ({ attempt }) => (
  <div className="col-md-6 mb-3">
    <div className="card">
      <div className="card-header d-flex justify-content-between">
        <strong>{attempt.testDate}</strong>
        <span className="badge bg-blue-lt">Cycle {attempt.cycle}</span>
      </div>

      <div className="card-body row text-center">
        <Metric label="Score" value={attempt.score} />
        <Metric label="Correct" value={attempt.correct} color="text-success" />
        <Metric label="Wrong" value={attempt.wrong} color="text-danger" />
        <Metric label="Skipped" value={attempt.skipped} color="text-warning" />
      </div>
    </div>
  </div>
);

const StatCard = ({ label, value, color = "" }) => (
  <div className="col-6 col-md-3">
    <div className="card text-center">
      <div className="card-body">
        <div className="text-muted">{label}</div>
        <div className={`fs-2 fw-bold ${color}`}>{value}</div>
      </div>
    </div>
  </div>
);

const Metric = ({ label, value, color = "" }) => (
  <div className="col">
    <div className="text-muted">{label}</div>
    <div className={`fw-bold fs-4 ${color}`}>{value}</div>
  </div>
);

const Empty = () => <div className="text-muted px-2">No records available</div>;

export default EmployeeTestOverview;

// import React from "react";
// import { Line } from "react-chartjs-2";
// import {
//   Chart as ChartJS,
//   LineElement,
//   PointElement,
//   LinearScale,
//   CategoryScale,
//   Tooltip,
// } from "chart.js";

// ChartJS.register(
//   LineElement,
//   PointElement,
//   LinearScale,
//   CategoryScale,
//   Tooltip
// );

// /* ------------------ DUMMY DATA ------------------ */

// const user = {
//   userName: "Rahul Sharma",
//   department: "Engineering",
//   photograph: "https://i.pravatar.cc/150?img=12",
//   rank: 1,
// };

// const attemptsTimeline = [
//   { label: "Aug 15", score: 80 },
//   { label: "Aug 28", score: 88 },
//   { label: "Sep 15", score: 92 },
//   { label: "Sep 28", score: 96 },
// ];

// const stats = {
//   attempts: 4,
//   correct: 92,
//   wrong: 18,
//   skipped: 10,
//   totalQuestions: 120,
//   totalTimeMinutes: 110,
// };

// /* ------------------ HELPERS ------------------ */

// const rankBadge = (rank) => {
//   if (rank === 1) return "bg-yellow text-dark";
//   if (rank === 2) return "bg-secondary";
//   if (rank === 3) return "bg-orange";
//   return "bg-blue-lt";
// };

// const calculateConsistency = (scores) => {
//   const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
//   const variance =
//     scores.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / scores.length;

//   return Math.max(0, Math.round(100 - variance));
// };

// /* ------------------ CHART ------------------ */

// const chartData = {
//   labels: attemptsTimeline.map((a) => a.label),
//   datasets: [
//     {
//       data: attemptsTimeline.map((a) => a.score),
//       tension: 0.4,
//       borderWidth: 3,
//       pointRadius: 5,
//     },
//   ],
// };

// const chartOptions = {
//   plugins: { legend: { display: false } },
//   scales: { y: { beginAtZero: true, max: 100 } },
// };

// /* ------------------ COMPONENT ------------------ */

// function EvaluationOverview() {
//   const scores = attemptsTimeline.map((a) => a.score);
//   const consistencyScore = calculateConsistency(scores);

//   const accuracy = Math.round((stats.correct / stats.totalQuestions) * 100);
//   const avgTime = (stats.totalTimeMinutes / stats.totalQuestions).toFixed(1);

//   return (
//     <div className="page">
//       <div className="container-xl">
//         {/* Header */}
//         <div className="page-header mb-3">
//           <h2 className="page-title">Evaluation Overview</h2>
//         </div>

//         {/* User Card */}
//         <div className="card mb-3">
//           <div className="card-body py-2 d-flex align-items-center">
//             <span
//               className="avatar avatar-md me-3"
//               style={{ backgroundImage: `url(${user.photograph})` }}
//             />
//             <div className="flex-grow-1">
//               <div className="fw-bold">{user.userName}</div>
//               <div className="text-muted fs-7">{user.department}</div>
//             </div>
//             <span className={`badge ${rankBadge(user.rank)}`}>
//               #{user.rank}
//             </span>
//           </div>
//         </div>

//         {/* Quick Stats */}
//         <div className="row row-cards mb-3">
//           <MiniStat label="Attempts" value={stats.attempts} />
//           <MiniStat
//             label="Correct"
//             value={stats.correct}
//             color="text-success"
//           />
//           <MiniStat label="Wrong" value={stats.wrong} color="text-danger" />
//           <MiniStat
//             label="Skipped"
//             value={stats.skipped}
//             color="text-warning"
//           />
//         </div>

//         {/* Score Trend */}
//         <div className="card mb-3">
//           <div className="card-header">
//             <h3 className="card-title">Score Trend</h3>
//           </div>
//           <div className="card-body">
//             <Line data={chartData} options={chartOptions} height={80} />
//           </div>
//         </div>

//         {/* Insights */}
//         <div className="row mb-4">
//           <InsightCard
//             title="Accuracy"
//             value={`${accuracy}%`}
//             sub="Correct / Total"
//             color="text-success"
//           />
//           <InsightCard
//             title="Avg Time / Question"
//             value={`${avgTime} min`}
//             sub="Speed efficiency"
//           />
//           <InsightCard
//             title="Consistency Score"
//             value={consistencyScore}
//             sub="Lower variance = better"
//             color={consistencyScore > 80 ? "text-success" : "text-warning"}
//           />
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ------------------ SMALL COMPONENTS ------------------ */

// function MiniStat({ label, value, color = "" }) {
//   return (
//     <div className="col-6 col-md-3">
//       <div className="card">
//         <div className="card-body text-center py-2">
//           <div className="text-muted fs-7">{label}</div>
//           <div className={`fw-bold ${color}`}>{value}</div>
//         </div>
//       </div>
//     </div>
//   );
// }

// function InsightCard({ title, value, sub, color = "" }) {
//   return (
//     <div className="col-md-4">
//       <div className="card">
//         <div className="card-body text-center">
//           <div className="text-muted">{title}</div>
//           <div className={`fs-2 fw-bold ${color}`}>{value}</div>
//           <div className="text-muted fs-7">{sub}</div>
//         </div>
//       </div>
//     </div>
//   );
// }

// export default EvaluationOverview;
// import React from "react";
// import { Line } from "react-chartjs-2";
// import {
//   Chart as ChartJS,
//   LineElement,
//   PointElement,
//   LinearScale,
//   CategoryScale,
//   Tooltip,
// } from "chart.js";

// ChartJS.register(
//   LineElement,
//   PointElement,
//   LinearScale,
//   CategoryScale,
//   Tooltip
// );

// /* ------------------ DUMMY DATA ------------------ */

// const user = {
//   userName: "Rahul Sharma",
//   department: "Engineering",
//   photograph: "https://i.pravatar.cc/150?img=12",
//   rank: 1,
// };

// const attempts = [
//   {
//     testDate: "2026-01-15",
//     status: "SUBMITTED",
//     totalQuestions: 30,
//     correctAnswers: 22,
//     wrongAnswers: 5,
//     notAnswered: 3,
//     testScore: 22,
//     testTime: "26:40",
//   },
//   {
//     testDate: "2025-12-28",
//     status: "SUBMITTED",
//     totalQuestions: 30,
//     correctAnswers: 18,
//     wrongAnswers: 7,
//     notAnswered: 5,
//     testScore: 18,
//     testTime: "29:10",
//   },
// ];

// const attemptsTimeline = [
//   { label: "Aug 15", score: 80 },
//   { label: "Aug 28", score: 88 },
//   { label: "Sep 15", score: 92 },
//   { label: "Sep 28", score: 96 },
// ];

// /* ------------------ HELPERS ------------------ */

// const rankBadge = (rank) => {
//   if (rank === 1) return "bg-yellow text-dark";
//   if (rank === 2) return "bg-secondary";
//   if (rank === 3) return "bg-orange";
//   return "bg-blue-lt";
// };

// const statusBadge = (status) => {
//   if (status === "SUBMITTED") return "bg-success-lt";
//   if (status === "STARTED") return "bg-warning-lt";
//   return "bg-danger-lt";
// };

// const calculateConsistency = (scores) => {
//   const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
//   const variance =
//     scores.reduce((a, b) => a + Math.pow(b - avg, 2), 0) / scores.length;
//   return Math.max(0, Math.round(100 - variance));
// };

// /* ------------------ CHART ------------------ */

// const chartData = {
//   labels: attemptsTimeline.map((a) => a.label),
//   datasets: [
//     {
//       data: attemptsTimeline.map((a) => a.score),
//       tension: 0.4,
//       borderWidth: 3,
//       pointRadius: 5,
//     },
//   ],
// };

// const chartOptions = {
//   plugins: { legend: { display: false } },
//   scales: { y: { beginAtZero: true, max: 100 } },
// };

// /* ------------------ COMPONENT ------------------ */

// function EmployeeTestOverview() {
//   if (!attempts.length) {
//     return <p className="text-center">No test attempts found</p>;
//   }

//   const sortedAttempts = [...attempts].sort(
//     (a, b) => new Date(b.testDate) - new Date(a.testDate)
//   );

//   const currentAttempt = sortedAttempts[0];
//   const previousAttempts = sortedAttempts.slice(1);

//   const scores = attemptsTimeline.map((a) => a.score);
//   const consistencyScore = calculateConsistency(scores);

//   const accuracy = Math.round(
//     (currentAttempt.correctAnswers / currentAttempt.totalQuestions) * 100
//   );

//   return (
//     <div className="page">
//       <div className="container-xl">
//         {/* HEADER */}
//         <div className="page-header mb-3">
//           <h2 className="page-title">Your Test Overview</h2>
//           <p className="text-muted">Master what matters</p>
//         </div>

//         {/* USER CARD */}
//         <div className="card mb-3">
//           <div className="card-body py-2 d-flex align-items-center">
//             <span
//               className="avatar avatar-md me-3"
//               style={{ backgroundImage: `url(${user.photograph})` }}
//             />
//             <div className="flex-grow-1">
//               <div className="fw-bold">{user.userName}</div>
//               <div className="text-muted fs-7">{user.department}</div>
//             </div>
//             <span className={`badge ${rankBadge(user.rank)}`}>
//               #{user.rank}
//             </span>
//           </div>
//         </div>

//         {/* CURRENT ATTEMPT SUMMARY */}
//         <div className="row row-cards mb-3">
//           <MiniStat label="Score" value={currentAttempt.testScore} />
//           <MiniStat
//             label="Correct"
//             value={currentAttempt.correctAnswers}
//             color="text-success"
//           />
//           <MiniStat
//             label="Wrong"
//             value={currentAttempt.wrongAnswers}
//             color="text-danger"
//           />
//           <MiniStat
//             label="Skipped"
//             value={currentAttempt.notAnswered}
//             color="text-warning"
//           />
//         </div>

//         {/* SCORE TREND */}
//         <div className="card mb-3">
//           <div className="card-header">
//             <h3 className="card-title">Score Trend</h3>
//           </div>
//           <div className="card-body">
//             <Line data={chartData} options={chartOptions} height={80} />
//           </div>
//         </div>

//         {/* INSIGHTS */}
//         <div className="row mb-4">
//           <InsightCard
//             title="Accuracy"
//             value={`${accuracy}%`}
//             sub="Latest attempt"
//             color="text-success"
//           />
//           <InsightCard
//             title="Consistency Score"
//             value={consistencyScore}
//             sub="Across attempts"
//           />
//           <InsightCard
//             title="Time Taken"
//             value={currentAttempt.testTime}
//             sub="Latest test"
//           />
//         </div>

//         {/* PREVIOUS ATTEMPTS */}
//         <div className="card">
//           <div className="card-header">
//             <h3 className="card-title">Previous Attempts</h3>
//           </div>
//           <div className="card-body">
//             {previousAttempts.length === 0 ? (
//               <div className="text-muted">No previous attempts</div>
//             ) : (
//               <div className="row row-cards">
//                 {previousAttempts.map((item, index) => (
//                   <div className="col-md-6" key={index}>
//                     <div className="card card-md">
//                       <div className="card-header d-flex justify-content-between">
//                         <strong>{item.testDate}</strong>
//                         <span className={`badge ${statusBadge(item.status)}`}>
//                           {item.status}
//                         </span>
//                       </div>
//                       <div className="card-body">
//                         <div className="row text-center">
//                           <AttemptMetric label="Score" value={item.testScore} />
//                           <AttemptMetric
//                             label="Correct"
//                             value={item.correctAnswers}
//                             color="text-success"
//                           />
//                           <AttemptMetric
//                             label="Wrong"
//                             value={item.wrongAnswers}
//                             color="text-danger"
//                           />
//                           <AttemptMetric
//                             label="Skipped"
//                             value={item.notAnswered}
//                             color="text-warning"
//                           />
//                         </div>

//                         <div className="mt-3">
//                           <div className="progress progress-sm">
//                             <div
//                               className="progress-bar bg-success"
//                               style={{
//                                 width: `${
//                                   (item.correctAnswers / item.totalQuestions) *
//                                   100
//                                 }%`,
//                               }}
//                             />
//                           </div>
//                           <small className="text-muted">
//                             Accuracy{" "}
//                             {Math.round(
//                               (item.correctAnswers / item.totalQuestions) * 100
//                             )}
//                             %
//                           </small>
//                         </div>
//                       </div>
//                     </div>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// }

// /* ------------------ SMALL COMPONENTS ------------------ */

// const MiniStat = ({ label, value, color = "" }) => (
//   <div className="col-6 col-md-3">
//     <div className="card">
//       <div className="card-body text-center py-2">
//         <div className="text-muted fs-7">{label}</div>
//         <div className={`fw-bold ${color}`}>{value}</div>
//       </div>
//     </div>
//   </div>
// );

// const InsightCard = ({ title, value, sub, color = "" }) => (
//   <div className="col-md-4">
//     <div className="card">
//       <div className="card-body text-center">
//         <div className="text-muted">{title}</div>
//         <div className={`fs-2 fw-bold ${color}`}>{value}</div>
//         <div className="text-muted fs-7">{sub}</div>
//       </div>
//     </div>
//   </div>
// );

// const AttemptMetric = ({ label, value, color = "" }) => (
//   <div className="col-3">
//     <div className="text-muted fs-7">{label}</div>
//     <div className={`fw-bold ${color}`}>{value}</div>
//   </div>
// );

// export default EmployeeTestOverview;
